import React, { useCallback, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Button, Drawer, Empty, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import {
	SorterPlanSlotWithZones,
	ZoneNode,
} from "../../../types/sortingMachine";
import ZoneTreeNode from "./ZoneTreeNode";
import useLang from "../../../hooks/useLang";

interface ZoneTreeDrawerProps {
	open: boolean;
	slotId: number | null;
	slotDescription: string;
	zoneTree: ZoneNode[];
	allSlots: SorterPlanSlotWithZones[];
	onSave: (slotId: number, zoneIds: number[]) => void;
	onClose: () => void;
}

const DRAWER_PRIMARY_BUTTON_STYLE: CSSProperties = {
	backgroundColor: "#4F16C2",
	borderColor: "#4F16C2",
	color: "#ffffff",
};

const ZoneTreeDrawer = ({
	open,
	slotId,
	slotDescription,
	zoneTree,
	allSlots,
	onSave,
	onClose,
}: ZoneTreeDrawerProps) => {
	const { strings } = useLang("sortingMachineTab");
	const [searchTerm, setSearchTerm] = useState("");
	const [localSelectedIds, setLocalSelectedIds] = useState<Set<number>>(
		new Set()
	);
	const [initialized, setInitialized] = useState(false);

	const currentSlot = useMemo(
		() => allSlots.find((s) => s.id === slotId) ?? null,
		[allSlots, slotId]
	);

	if (open && !initialized) {
		const initial = new Set(currentSlot?.zones.map((z) => z.zoneId) ?? []);
		setLocalSelectedIds(initial);
		setInitialized(true);
		setSearchTerm("");
	}

	if (!open && initialized) {
		setInitialized(false);
	}

	const disabledZoneMap = useMemo(() => {
		const map = new Map<number, string>();
		for (const slot of allSlots) {
			if (slot.id === slotId) continue;
			for (const zone of slot.zones) {
				map.set(
					zone.zoneId,
					`${strings?.zoneDrawer?.assignedTo ?? "Assigned to Slot"} ${
						slot.slotNumber
					} — ${slot.displayName}`
				);
			}
		}
		return map;
	}, [allSlots, slotId, strings?.zoneDrawer?.assignedTo]);

	const handleToggle = useCallback((zoneIds: number[], checked: boolean) => {
		setLocalSelectedIds((prev) => {
			const next = new Set(prev);
			for (const id of zoneIds) {
				if (checked) {
					next.add(id);
				} else {
					next.delete(id);
				}
			}
			return next;
		});
	}, []);

	const handleDisabledZoneAttempt = useCallback(
		(zoneName: string, assignmentDetail: string) => {
			const template =
				strings?.zoneDrawer?.zoneInUseNotification ??
				"{zoneName} — {detail}";
			const message = template
				.replace("{zoneName}", zoneName)
				.replace("{detail}", assignmentDetail);
			toast.info(message);
		},
		[strings?.zoneDrawer?.zoneInUseNotification]
	);

	const handleSave = useCallback(() => {
		if (slotId === null) return;
		onSave(slotId, Array.from(localSelectedIds));
	}, [slotId, localSelectedIds, onSave]);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchTerm(e.target.value);
		},
		[]
	);

	const selectedCount = localSelectedIds.size;

	const drawerTitle = `${slotDescription} — ${selectedCount} ${
		strings?.zoneDrawer?.zonesSelected ?? "zones selected"
	}`;

	const footerContent = (
		<div className="flex justify-end gap-2">
			<Button onClick={onClose}>
				{strings?.zoneDrawer?.cancel ?? "Cancel"}
			</Button>
			<Button
				type="primary"
				onClick={handleSave}
				style={DRAWER_PRIMARY_BUTTON_STYLE}
			>
				{strings?.zoneDrawer?.save ?? "Save"}
			</Button>
		</div>
	);

	return (
		<Drawer
			title={drawerTitle}
			placement="right"
			width={480}
			open={open}
			onClose={onClose}
			footer={footerContent}
			destroyOnClose={false}
		>
			<div className="mb-3">
				<Input
					prefix={<SearchOutlined />}
					placeholder={
						strings?.zoneDrawer?.searchPlaceholder ??
						"Search zones..."
					}
					value={searchTerm}
					onChange={handleSearchChange}
					allowClear
				/>
			</div>

			<div
				className="overflow-y-auto"
				style={{ maxHeight: "calc(100vh - 220px)" }}
			>
				{zoneTree.length === 0 ? (
					<Empty
						description={
							strings?.zoneDrawer?.noZones ?? "No zones available"
						}
					/>
				) : (
					zoneTree.map((root) => (
						<ZoneTreeNode
							key={root.id}
							node={root}
							selectedZoneIds={localSelectedIds}
							disabledZoneMap={disabledZoneMap}
							onToggle={handleToggle}
							onDisabledZoneAttempt={handleDisabledZoneAttempt}
							searchTerm={searchTerm}
							depth={0}
						/>
					))
				)}
			</div>
		</Drawer>
	);
};

export default ZoneTreeDrawer;
